import React, { memo, useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';

import { ReactComponent as DocumentIcon } from 'assets/icons/DocumentIcon.svg';
import { useStyles } from './styles';

export interface IImagePdfPreviewProps {
  url: string;
  alt?: string;
  imageClassName?: string;
}

const PDF_MIME_TYPE = 'application/pdf';

export const isPdfUrl = (url: string): boolean => {
  if (url.startsWith('data:application/pdf')) return true;
  try {
    const pathname = new URL(url).pathname;
    return pathname.toLowerCase().endsWith('.pdf');
  } catch {
    return url.split('?')[0].toLowerCase().endsWith('.pdf');
  }
};

const PdfPreview = ({ url }: { url: string }) => {
  const classes = useStyles();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);

  // Presigned URLs often carry a signed `response-content-disposition=attachment` that Chrome
  // honours inside an <iframe> and turns into an immediate "Save As". fetch() ignores both
  // Content-Disposition and Content-Type, so we re-wrap the bytes in a blob we tag as
  // application/pdf ourselves — the resulting blob: URL always renders inline. Only if the
  // fetch itself fails (CORS, network, non-2xx) do we fall back to a clickable document icon.
  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    setBlobUrl(null);
    setLoadError(false);

    const load = async () => {
      try {
        let blob: Blob;
        if (url.startsWith('data:')) {
          const base64Data = url.split(',')[1];
          const binary = atob(base64Data);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          blob = new Blob([bytes], { type: PDF_MIME_TYPE });
        } else {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
          blob = new Blob([await response.arrayBuffer()], { type: PDF_MIME_TYPE });
        }
        if (!cancelled) {
          objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
        }
      } catch (err) {
        if (!cancelled) setLoadError(true);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  return (
    <div className={classes.pdfPreview}>
      {blobUrl ? (
        <iframe src={blobUrl} title="PDF Preview" className={classes.pdfIframe} />
      ) : (
        <div className={classes.pdfPlaceholder} onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>
          {loadError ? (
            <>
              <DocumentIcon className={classes.pdfFallbackIcon} />
              <span className={classes.pdfLabel}>PDF</span>
            </>
          ) : (
            <CircularProgress size={24} />
          )}
        </div>
      )}
    </div>
  );
};

const ImagePdfPreview = ({ url, alt = '', imageClassName }: IImagePdfPreviewProps) => {
  const classes = useStyles();

  if (isPdfUrl(url)) {
    return <PdfPreview url={url} />;
  }

  return <img src={url} alt={alt} className={imageClassName ?? classes.image} />;
};

export default memo(ImagePdfPreview);
